/**
 * The Levels panel body: header (title, count, ⋯, close), search, tabs with
 * on/total badges, a scrolling row list, and a footer with the tab's
 * Show all / Hide all. Works as a desktop popover and as a mobile bottom sheet;
 * the shell (position, sheet chrome) is LevelsToggle's job.
 */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Activity, BarChart3, Eye, EyeOff, Layers, MapPin, MoreHorizontal, Ruler, Search, Settings2, Sigma, X,
} from 'lucide-react';
import { LEVELS_TABS, useLevelsPanelSessionStore, useLevelsPanelUiStore, type LevelsTab } from '../../store/levelsPanelUiStore';
import type { LevelRow, LevelRowsModel, SwitchRow } from './useLevelRows';

export const TAB_META: Record<LevelsTab, { label: string; icon: React.ReactNode }> = {
  price: { label: 'Price', icon: <Ruler size={13} /> },
  indicators: { label: 'Indicators', icon: <Activity size={13} /> },
  volume: { label: 'Volume', icon: <BarChart3 size={13} /> },
  gamma: { label: 'Gamma', icon: <Sigma size={13} /> },
  profile: { label: 'Profile', icon: <Layers size={13} /> },
  markers: { label: 'Markers', icon: <MapPin size={13} /> },
};

const isSwitch = (r: LevelRow): r is SwitchRow => r.kind === 'switch';

export const matchesQuery = (r: LevelRow, q: string): boolean => {
  if (!q) return true;
  const hay = `${r.label} ${r.description ?? ''} ${r.keywords ?? ''} ${r.group ?? ''} ${r.kind === 'switch' ? r.settingsLabel ?? '' : ''}`.toLowerCase();
  return q.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
};

export const tabCounts = (rows: LevelRow[], tab: LevelsTab) => {
  const sw = rows.filter((r): r is SwitchRow => r.tab === tab && isSwitch(r));
  return { on: sw.filter((r) => r.on).length, total: sw.length };
};

interface LevelsPanelProps {
  model: LevelRowsModel;
  mobile: boolean;
  onClose: () => void;
  /** Sheet drag handle / header pointer handlers (mobile swipe-to-close). */
  dragHandlers?: React.HTMLAttributes<HTMLDivElement>;
}

const SwitchRowView: React.FC<{ row: SwitchRow; showTab?: boolean }> = ({ row, showTab }) => {
  const expanded = useLevelsPanelSessionStore((s) => !!s.expanded[row.id]);
  const toggleExpanded = useLevelsPanelSessionStore((s) => s.toggleExpanded);
  const gearId = useId();
  return (
    <div className={`levels-row ${row.on ? 'is-on' : ''} ${row.disabled ? 'is-disabled' : ''}`} data-row-id={row.id}>
      <div className="levels-row-main">
        <button
          type="button"
          role="switch"
          aria-checked={row.on}
          aria-label={row.label}
          className={`levels-item ${row.on ? 'active' : ''}`}
          onClick={() => row.set(!row.on)}
          disabled={row.disabled}
          title={row.description}
          data-testid={row.testId}
          data-nav=""
        >
          <span className="levels-row-dots" aria-hidden="true">{row.dots}</span>
          <span className="levels-row-text">
            <span className="levels-item-label">
              {row.label}
              {showTab && <span className="levels-row-tabtag">{TAB_META[row.tab].label}</span>}
            </span>
            {row.description && <span className="levels-row-desc">{row.description}</span>}
          </span>
          <span className="levels-switch" aria-hidden="true"><span className="levels-switch-knob" /></span>
        </button>
        {row.settings && (
          <button
            type="button"
            className={`levels-gear ${expanded ? 'is-open' : ''}`}
            aria-expanded={expanded}
            aria-controls={gearId}
            aria-label={`${row.settingsLabel ?? row.label} settings`}
            title={row.settingsLabel ?? 'Settings'}
            onClick={() => toggleExpanded(row.id)}
            data-testid={row.testId ? `${row.testId}-gear` : undefined}
          >
            <Settings2 size={13} />
          </button>
        )}
        {/* Same column for every switch, gear or not. */}
        {!row.settings && <span className="levels-gear-spacer" aria-hidden="true" />}
      </div>
      {row.settings && expanded && (
        <div id={gearId} className="levels-gear-body" role="group" aria-label={row.settingsLabel ?? `${row.label} settings`}>
          {row.settings}
        </div>
      )}
      {row.after}
    </div>
  );
};

const ControlRowView: React.FC<{ row: Extract<LevelRow, { kind: 'control' }>; showTab?: boolean }> = ({ row, showTab }) => (
  <div className="levels-row levels-row-control" data-row-id={row.id} title={row.description}>
    <div className="levels-row-main">
      <div className="levels-control-head">
        <span className="levels-row-text">
          <span className="levels-item-label">
            {row.label}
            {showTab && <span className="levels-row-tabtag">{TAB_META[row.tab].label}</span>}
          </span>
          {row.description && <span className="levels-row-desc">{row.description}</span>}
        </span>
        {row.control && <span className="levels-row-control-slot">{row.control}</span>}
      </div>
    </div>
    {row.body}
  </div>
);

const RowList: React.FC<{ rows: LevelRow[]; showTab?: boolean }> = ({ rows, showTab }) => {
  let lastGroup: string | undefined;
  return (
    <>
      {rows.map((r) => {
        const heading = !showTab && r.group && r.group !== lastGroup ? r.group : null;
        lastGroup = r.group;
        return (
          <React.Fragment key={r.id}>
            {heading && <div className="levels-group-heading">{heading}</div>}
            {isSwitch(r) ? <SwitchRowView row={r} showTab={showTab} /> : <ControlRowView row={r} showTab={showTab} />}
          </React.Fragment>
        );
      })}
    </>
  );
};

const LevelsPanel: React.FC<LevelsPanelProps> = ({ model, mobile, onClose, dragHandlers }) => {
  const { rows } = model;
  const activeTab = useLevelsPanelUiStore((s) => s.activeTab);
  const setActiveTab = useLevelsPanelUiStore((s) => s.setActiveTab);
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<LevelsTab, HTMLButtonElement | null>>>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [tabsMore, setTabsMore] = useState({ left: false, right: false });
  const baseId = useId();

  // Fade the tab strip's edge(s) while more tabs are hidden that way.
  const measureTabs = () => {
    const el = tabsRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setTabsMore((p) => (p.left === left && p.right === right ? p : { left, right }));
  };
  useEffect(() => {
    measureTabs();
    window.addEventListener('resize', measureTabs);
    return () => window.removeEventListener('resize', measureTabs);
  }, []);

  const q = query.trim();
  const searching = q.length > 0;

  const tabRows = useMemo(() => rows.filter((r) => r.tab === activeTab), [rows, activeTab]);
  const results = useMemo(() => (searching ? rows.filter((r) => matchesQuery(r, q)) : []), [rows, q, searching]);
  const tabSwitches = tabRows.filter(isSwitch);
  const bulkable = tabSwitches.filter((r) => r.bulk !== false && (!r.disabled || r.vpKey));
  const allOn = bulkable.length > 0 && bulkable.every((r) => r.on);
  const allOff = bulkable.every((r) => !r.on);
  const totalOn = rows.filter(isSwitch).filter((r) => r.on).length;
  const totalSwitches = rows.filter(isSwitch).length;

  // Desktop: type to filter straight away. Mobile: no autofocus, it would pop the keyboard.
  useEffect(() => {
    if (!mobile) searchRef.current?.focus();
  }, [mobile]);

  // Close the ⋯ menu on an outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Keep the active tab visible in the sideways-scrolling strip.
  useEffect(() => {
    tabRefs.current[activeTab]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  // Each tab opens scrolled to the top.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [activeTab, searching]);

  const selectTab = (t: LevelsTab, focus = false) => {
    setActiveTab(t);
    if (searching) setQuery('');
    if (focus) tabRefs.current[t]?.focus();
  };

  const onTabKeyDown = (e: React.KeyboardEvent) => {
    const i = LEVELS_TABS.indexOf(activeTab);
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (i + 1) % LEVELS_TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + LEVELS_TABS.length) % LEVELS_TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = LEVELS_TABS.length - 1;
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      listRef.current?.querySelector<HTMLElement>('[data-nav]:not(:disabled)')?.focus();
      return;
    }
    if (next !== null) {
      e.preventDefault();
      selectTab(LEVELS_TABS[next], true);
    }
  };

  // Up / Down between rows (only from a row's own button, so number inputs
  // and selects keep their arrow keys).
  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const target = e.target as HTMLElement;
    if (!target.hasAttribute('data-nav')) return;
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-nav]') ?? [])
      .filter((el) => !(el as HTMLButtonElement).disabled);
    const i = items.indexOf(target);
    if (i < 0) return;
    e.preventDefault();
    if (e.key === 'ArrowDown') {
      if (i < items.length - 1) items[i + 1].focus();
    } else if (i > 0) items[i - 1].focus();
    else if (searching) searchRef.current?.focus();
    else tabRefs.current[activeTab]?.focus();
  };

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (menuOpen) { setMenuOpen(false); return; }
    if (query) { setQuery(''); searchRef.current?.focus(); return; }
    onClose();
  };

  // Results grouped by tab while searching.
  const groupedResults = LEVELS_TABS
    .map((t) => ({ t, items: results.filter((r) => r.tab === t) }))
    .filter((g) => g.items.length > 0);

  const panelId = `${baseId}-panel`;

  return (
    <div className="levels-panel" onKeyDown={onPanelKeyDown}>
      <div className="levels-panel-top">
        {/* Swipe-to-close grips only the handle + title bar, so the tab strip
            still pans sideways and the search field still takes touches. */}
        <div className="levels-sheet-drag" {...(mobile ? dragHandlers : {})}>
        {mobile && <div className="levels-sheet-handle" aria-hidden="true" />}
        <div className="levels-dropdown-header">
          <span className="levels-title">
            Price Levels
            <span className="levels-title-count" title="Switches on / total">{totalOn}/{totalSwitches}</span>
          </span>
          <div className="levels-header-actions" ref={menuRef}>
            <button
              type="button"
              className="levels-icon-btn"
              aria-label="More actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="levels-overflow"
            >
              <MoreHorizontal size={15} />
            </button>
            {menuOpen && (
              <div className="levels-menu" role="menu" aria-label="All levels">
                <button
                  type="button"
                  role="menuitem"
                  className="levels-menu-item"
                  disabled={model.levelsEnabled === model.levelsTotal}
                  onClick={() => { model.setAllLevelsVisible(true); setMenuOpen(false); }}
                  title="Every price level, indicator, volume and marker toggle on (same as the old Show All)"
                  data-testid="levels-show-everything"
                >
                  <Eye size={13} /> Show everything
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="levels-menu-item"
                  disabled={model.levelsEnabled === 0}
                  onClick={() => { model.setAllLevelsVisible(false); setMenuOpen(false); }}
                  title="Every price level, indicator, volume and marker toggle off (same as the old Hide All)"
                  data-testid="levels-hide-everything"
                >
                  <EyeOff size={13} /> Hide everything
                </button>
              </div>
            )}
            <button type="button" className="levels-icon-btn levels-close-btn" onClick={onClose} aria-label="Close" title="Close">
              <X size={15} />
            </button>
          </div>
        </div>
        </div>

        <div className="levels-search">
          <Search size={13} className="levels-search-icon" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            className="levels-search-input"
            placeholder="Search levels, indicators, settings"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                listRef.current?.querySelector<HTMLElement>('[data-nav]:not(:disabled)')?.focus();
              }
            }}
            aria-label="Search levels"
            aria-controls={panelId}
            data-testid="levels-search"
          />
          {query && (
            <button type="button" className="levels-search-clear" onClick={() => { setQuery(''); searchRef.current?.focus(); }} aria-label="Clear search">
              <X size={12} />
            </button>
          )}
        </div>

        <div
          ref={tabsRef}
          className={`levels-tabs ${tabsMore.left ? 'more-left' : ''} ${tabsMore.right ? 'more-right' : ''}`}
          role="tablist"
          aria-label="Level groups"
          onKeyDown={onTabKeyDown}
          onScroll={measureTabs}
        >
          {LEVELS_TABS.map((t) => {
            const { on, total } = tabCounts(rows, t);
            const selected = !searching && t === activeTab;
            return (
              <button
                key={t}
                ref={(el) => { tabRefs.current[t] = el; }}
                type="button"
                role="tab"
                id={`${baseId}-tab-${t}`}
                aria-selected={selected}
                aria-controls={panelId}
                tabIndex={t === activeTab ? 0 : -1}
                className={`levels-tab ${selected ? 'is-active' : ''}`}
                onClick={() => selectTab(t)}
                data-testid={`levels-tab-${t}`}
              >
                {TAB_META[t].icon}
                <span className="levels-tab-label">{TAB_META[t].label}</span>
                <span className="levels-tab-count" data-testid={`levels-tab-count-${t}`} aria-label={`${on} of ${total} on`}>
                  {on}/{total}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        ref={listRef}
        id={panelId}
        role="tabpanel"
        aria-labelledby={searching ? undefined : `${baseId}-tab-${activeTab}`}
        aria-label={searching ? 'Search results' : undefined}
        className="levels-list"
        onKeyDown={onListKeyDown}
        data-testid="levels-list"
      >
        {searching ? (
          groupedResults.length === 0 ? (
            <div className="levels-empty">No matches for "{q}"</div>
          ) : (
            groupedResults.map(({ t, items }) => (
              <div key={t} className="levels-result-group" data-testid={`levels-results-${t}`}>
                <div className="levels-group-heading levels-result-heading">{TAB_META[t].icon}{TAB_META[t].label}</div>
                <RowList rows={items} />
              </div>
            ))
          )
        ) : (
          <>
            {activeTab === 'gamma' && <div className="levels-tab-note">Status: {model.gexStatus}</div>}
            <RowList rows={tabRows} />
          </>
        )}
      </div>

      {!searching && bulkable.length > 0 && (
        <div className="levels-panel-footer">
          <button
            type="button"
            className="levels-all-btn"
            onClick={() => model.applyBulk(tabSwitches, true)}
            disabled={allOn}
            data-testid="levels-tab-show-all"
          >
            <Eye size={12} /> Show all {TAB_META[activeTab].label.toLowerCase()}
          </button>
          <button
            type="button"
            className="levels-all-btn"
            onClick={() => model.applyBulk(tabSwitches, false)}
            disabled={allOff}
            data-testid="levels-tab-hide-all"
          >
            <EyeOff size={12} /> Hide all
          </button>
        </div>
      )}
    </div>
  );
};

export default LevelsPanel;
